import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, Moon, Sun, X } from "lucide-react";
import { useStudio } from "@/context/StudioContext";

const LINKS = [
    { id: 0, key: "inicio" },
    { id: "#projetos", key: "portfolio" },
    { id: "#atelier", key: "sobre" },
    { id: "#contacto", key: "contactos" },
];

export const HeaderNav = () => {
    const { t, lang, setLang, theme, setTheme, scrollTo } = useStudio();
    const [active, setActive] = useState("");
    const [menuOpen, setMenuOpen] = useState(false);

    useEffect(() => {
        let obs;
        let tries = 0;
        const setup = () => {
            const sections = LINKS.filter((l) => typeof l.id === "string")
                .map((l) => document.querySelector(l.id))
                .filter(Boolean);
            if (sections.length < LINKS.filter((l) => typeof l.id === "string").length && tries < 40) {
                tries++;
                setTimeout(setup, 250);
                return;
            }
            obs = new IntersectionObserver(
                (entries) => entries.forEach((e) => e.isIntersecting && setActive(`#${e.target.id}`)),
                { rootMargin: "-35% 0px -55% 0px" }
            );
            sections.forEach((s) => obs.observe(s));
        };
        setup();
        return () => obs && obs.disconnect();
    }, []);

    const go = (id) => {
        setMenuOpen(false);
        scrollTo(id);
    };

    return (
        <header
            data-testid="header-nav"
            className="fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-[#024562]"
        >
            <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-2 px-4 sm:px-8 lg:px-16">
                <button
                    data-testid="nav-logo"
                    onClick={() => scrollTo(0)}
                    className="flex items-center"
                    aria-label="VALRIO ARQUITECTURA"
                >
                    <img src="/logo-valrio-mark.png" alt="" className="h-8 w-auto sm:h-9" />
                    <img src="/logo-valrio-wordmark.png" alt="VALRIO ARQUITECTURA" className="hidden h-9 w-auto min-[420px]:block sm:h-10" />
                </button>

                <nav className="hidden items-center gap-8 lg:flex" aria-label="Primary">
                    {LINKS.map((l) => (
                        <a
                            key={l.key}
                            href={l.id === 0 ? "#inicio" : l.id}
                            data-testid={`nav-link-${l.key}`}
                            onClick={(e) => {
                                e.preventDefault();
                                go(l.id);
                            }}
                            className={`group relative font-mono text-[11px] uppercase tracking-[0.22em] transition-colors duration-300 ${
                                active === l.id ? "text-white" : "text-[#EFE7D6]/65 hover:text-white"
                            }`}
                        >
                            {t.nav[l.key]}
                            <span
                                className={`absolute -bottom-1.5 left-0 h-px w-full origin-left bg-[#EFE7D6] transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] ${
                                    active === l.id ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"
                                }`}
                            />
                        </a>
                    ))}
                </nav>

                <div className="flex shrink-0 items-center gap-2 sm:gap-3">
                    <div
                        data-testid="lang-toggle"
                        className="flex items-center rounded-full border border-white/25 p-0.5"
                    >
                        {["pt", "en"].map((l) => (
                            <button
                                key={l}
                                data-testid={`lang-toggle-${l}`}
                                onClick={() => setLang(l)}
                                className={`rounded-full px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors duration-300 ${
                                    lang === l
                                        ? "bg-[#EFE7D6] text-[#024562]"
                                        : "text-[#EFE7D6]/70 hover:text-white"
                                }`}
                            >
                                {l}
                            </button>
                        ))}
                    </div>

                    <button
                        data-testid="theme-toggle"
                        onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
                        className="rounded-full border border-white/25 p-2 text-[#EFE7D6]/80 transition-colors duration-300 hover:text-white"
                        aria-label="Toggle theme"
                    >
                        <motion.span
                            key={theme}
                            initial={{ rotate: -60, opacity: 0 }}
                            animate={{ rotate: 0, opacity: 1 }}
                            transition={{ duration: 0.4, ease: "easeOut" }}
                            className="block"
                        >
                            {theme === "dark" ? <Sun className="h-3.5 w-3.5" /> : <Moon className="h-3.5 w-3.5" />}
                        </motion.span>
                    </button>

                    <button
                        data-testid="mobile-menu-toggle"
                        onClick={() => setMenuOpen((o) => !o)}
                        className="rounded-full border border-white/25 p-2 text-[#EFE7D6]/80 transition-colors duration-300 hover:text-white lg:hidden"
                        aria-label={menuOpen ? "Fechar menu" : "Abrir menu"}
                        aria-expanded={menuOpen}
                    >
                        {menuOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
                    </button>
                </div>
            </div>

            <AnimatePresence>
                {menuOpen && (
                    <motion.nav
                        data-testid="mobile-menu"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: "easeOut" }}
                        className="overflow-hidden border-t border-white/10 bg-[#024562] lg:hidden"
                        aria-label="Mobile"
                    >
                        <div className="flex flex-col px-4 py-3 sm:px-8">
                            {LINKS.map((l) => (
                                <a
                                    key={l.key}
                                    href={l.id === 0 ? "#inicio" : l.id}
                                    data-testid={`mobile-nav-link-${l.key}`}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        go(l.id);
                                    }}
                                    className={`border-b border-white/5 py-3.5 text-left font-mono text-xs uppercase tracking-[0.22em] transition-colors duration-300 last:border-b-0 ${
                                        active === l.id ? "text-white" : "text-[#EFE7D6]/65 hover:text-white"
                                    }`}
                                >
                                    {t.nav[l.key]}
                                </a>
                            ))}
                        </div>
                    </motion.nav>
                )}
            </AnimatePresence>
        </header>
    );
};
